import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, Zap, FileText, Check } from "lucide-react";
import { api, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import FdpListsPanel from "@/components/FdpListsPanel";

export default function ServiceRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/service-requests", { params: { status: filter } });
    setRequests(r.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const convert = async (id) => {
    const ok = window.confirm("Converter esta solicitação em um orçamento?");
    if (!ok) return;
    try {
      const r = await api.post(`/service-requests/${id}/convert`);
      toast.success(`Orçamento Nº ${r.data.number} criado`);
      load();
    } catch (e) {
      toast.error("Erro ao converter");
    }
  };

  const acceptFdp = async (r) => {
    if (!window.confirm(`Ativar ${r.school_name} no Faz Depois Paga? As solicitações pendentes dela viram a Lista FDP.`)) return;
    try {
      const res = await api.post(`/schools/${r.school_id}/fdp-accept`);
      toast.success(`Escola ativada no FDP — ${res.data.moved} item(ns) na lista`);
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ativar");
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Inbox}
        title="Solicitações das escolas"
        subtitle="Novos pedidos de serviço enviados pelas escolas"
      />

      <FdpListsPanel />

      <div className="flex gap-2 overflow-x-auto">
        {[
          { v: "all", l: "Todos" },
          { v: "pendente", l: "Pendentes" },
          { v: "convertida", l: "Convertidas" },
          { v: "cancelada", l: "Canceladas" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            data-testid={`sr-filter-${t.v}`}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold ${
              filter === t.v ? "bg-[#1e3a8a] text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-10 text-center">
          <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Nenhuma solicitação encontrada.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div
              key={r.id}
              data-testid={`sr-card-${r.id}`}
              className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-bold text-slate-900">{r.title}</h3>
                    {r.priority === "urgente" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        <Zap className="w-3 h-3" />
                        URGENTE
                      </span>
                    )}
                    {r.fdp_candidate && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Fora do FDP</span>
                    )}
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        r.status === "convertida" || r.status === "lista_fdp"
                          ? "bg-green-100 text-green-700"
                          : r.status === "cancelada"
                          ? "bg-red-100 text-red-700"
                          : "bg-orange-100 text-orange-700"
                      }`}
                    >
                      {r.status === "convertida" ? "Convertida" : r.status === "lista_fdp" ? "Na Lista FDP" : r.status === "cancelada" ? "Cancelada" : "Pendente"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {r.school_name} • {formatDateBR(r.created_at?.slice(0, 10))}
                  </p>
                  {r.description && (
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{r.description}</p>
                  )}
                </div>
                {r.status === "pendente" && r.fdp_candidate && (
                  <Button
                    onClick={() => acceptFdp(r)}
                    variant="outline"
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid={`sr-accept-fdp-${r.id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Aceitar e ativar no FDP
                  </Button>
                )}
                {r.status === "pendente" && (
                  <Button
                    onClick={() => convert(r.id)}
                    className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                    data-testid={`sr-convert-${r.id}`}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Converter em orçamento
                  </Button>
                )}
                {r.status === "convertida" && r.converted_budget_id && (
                  <Link
                    to={`/orcamentos/${r.converted_budget_id}`}
                    data-testid={`sr-view-budget-${r.id}`}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm font-semibold"
                  >
                    <Check className="w-4 h-4" />
                    Ver orçamento
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
